import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withStyles, makeStyles, createStyles } from "@material-ui/core/styles";
import {
  Grid,
  Checkbox,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import moment from "moment-timezone";
import SingleSelect from "components/Select/SingleSelect";
import {
  setDateRangePicTreeNtt,
  setDateRangePicTreeMinMax,
  setSelectedSeasonNtt,
  setSelectedAreaLayerNtt,
  getSeasonDataNtt,
} from "actions";
import {
  REGULAR_SEASON_IDS,
  isRegularSeasonId,
} from "../../../constants/pictreeRegularSeasonIds";

const REGULAR_BY_AREA_PAGE = "/game/pictree/ntt/photos/by-area";
const REGULAR_LAYER_OPTIONS = [
  { name: "都道府県", value: "regular_1_l1" },
  { name: "医療圏", value: "regular_1_l2" },
  { name: "市区町村", value: "regular_1_l3" },
];
export const NTT_ME_JISAI_PRO_SEASON_ID = "cmw1ktjnp0000qizc96pjev7m";
export const NTT_ME_HOKKAIDO_2026_SEASON_ID = "cmr0bus0w00010azches0f7ak";
const JST_TIMEZONE = "Asia/Tokyo";

// Redux stores the selected season but not whether it was selected by the user
// or initialized automatically. Keep that provenance local to the shared
// selector so an old automatic default can be refreshed after navigation
// without changing the Redux state shape.
let automaticDefaultSeasonId = null;

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const getDateOnly = (value) => String(value || "").split(" ")[0];
const clampSeasonEndToToday = (seasonEnd, todayFormatted) => {
  if (!seasonEnd) {
    return todayFormatted;
  }

  const endDate = getDateOnly(seasonEnd);
  return moment(endDate).isAfter(todayFormatted) ? todayFormatted : endDate;
};

const getSeasonStart = (season) =>
  moment.tz(season.seasonStart, "YYYY-MM-DD HH:mm", JST_TIMEZONE);

export const isSeasonStarted = (season, now = moment().tz(JST_TIMEZONE)) =>
  !getSeasonStart(season).isAfter(moment(now).tz(JST_TIMEZONE));

export const getDefaultSeason = (
  seasonData,
  now = moment().tz(JST_TIMEZONE)
) => {
  const seasons = Array.isArray(seasonData) ? seasonData : [];
  const nowJst = moment(now).tz(JST_TIMEZONE);

  return seasons
    .filter((season) => isSeasonStarted(season, nowJst))
    .sort((a, b) => getSeasonStart(b).valueOf() - getSeasonStart(a).valueOf())[0] || null;
};

export const getSeasonDateRange = (
  season,
  now = moment().tz(JST_TIMEZONE)
) => {
  const nowJst = moment(now).tz(JST_TIMEZONE);
  const startDate = getSeasonStart(season).format("YYYY-MM-DD");
  const seasonEnd = moment.tz(season.seasonEnd, "YYYY-MM-DD HH:mm", JST_TIMEZONE);
  const todayDate = nowJst.format("YYYY-MM-DD");
  const endDate = seasonEnd.isBefore(nowJst, "day") ? seasonEnd.format("YYYY-MM-DD") : todayDate;

  return [startDate, moment(endDate).isBefore(startDate) ? startDate : endDate];
};

export const getSeasonBounds = (season) => [
  getSeasonStart(season).format("YYYY-MM-DD"),
  moment
    .tz(season.seasonEnd, "YYYY-MM-DD HH:mm", JST_TIMEZONE)
    .format("YYYY-MM-DD"),
];

const sameValues = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const useStyles = makeStyles((theme) =>
  createStyles({
    calendarWrapper: {
      paddingLeft: 8,
    },
    root: {
      backgroundColor: "#fafafa",
      zIndex: 1000,
      paddingTop: 16,
      paddingLeft: 8,
      marginTop: 0,
    },
    chip: {
      margin: theme.spacing(0.5),
      fontSize: "0.75rem",
      height: 24,
    },
    autocomplete: {
      width: 510,
      marginTop: -8,
    },
    seasonTextField: {
      "& .MuiOutlinedInput-root": {
        padding: "1px 8px",
      },
      "& .MuiOutlinedInput-input": {
        height: "1.2em",
      },
    },
  })
);

const PeriodNtt = ({
  setDateRangePicTreeNtt,
  setDateRangePicTreeMinMax,
  setSelectedSeasonNtt,
  setSelectedAreaLayerNtt,
  getSeasonDataNtt,
  seasonData,
  selectedSeason,
  selectedAreaLayerId,
  dateRangePicTreeNtt,
  dateRangePicTreeMinMax,
  singlePeriod,
  currentPage,
  onBudgetSelectionChange,
}) => {
  const classes = useStyles();
  const currentJst = moment().tz(JST_TIMEZONE);
  const currentDateTime = currentJst.format("YYYY-MM-DD HH:mm");
  const today = currentDateTime;
  const todayFormatted = moment(today).format("YYYY-MM-DD");
  const [seasonOption, setSeasonOption] = useState({ name: "", value: "" });

  const getSeasonById = useCallback(
    (seasonId) =>
      seasonData.find((season) => season.seasonId === seasonId) || null,
    [seasonData]
  );

  const applySeasonSelection = useCallback(
    (seasonId, { automatic = false } = {}) => {
      const season = getSeasonById(seasonId);
      const nowJst = moment.tz(currentDateTime, "YYYY-MM-DD HH:mm", JST_TIMEZONE);
      if (!season || !isSeasonStarted(season, nowJst)) {
        return false;
      }

      const nextSelection = [season];
      const nextRange = getSeasonDateRange(season, nowJst);
      const nextBounds = getSeasonBounds(season);
      const selectedIds = (selectedSeason || []).map((selected) => selected.seasonId);
      const nextIds = nextSelection.map((selected) => selected.seasonId);

      automaticDefaultSeasonId = automatic ? season.seasonId : null;

      if (seasonOption.value !== season.seasonId) {
        setSeasonOption({
          name: season.seasonName,
          value: season.seasonId,
        });
      }
      if (!sameValues(selectedIds, nextIds)) {
        setSelectedSeasonNtt(nextSelection);
      }

      if (!sameValues(dateRangePicTreeNtt || [], nextRange)) {
        setDateRangePicTreeNtt(nextRange);
      }
      if (!sameValues(dateRangePicTreeMinMax || [], nextBounds)) {
        setDateRangePicTreeMinMax(nextBounds);
      }

      if (
        isRegularSeasonId(season.seasonId) &&
        selectedAreaLayerId !== "regular_1_l1"
      ) {
        setSelectedAreaLayerNtt("regular_1_l1");
      }

      if (typeof onBudgetSelectionChange === "function") {
        onBudgetSelectionChange({
          selectedSeason: nextSelection,
          dateRange: nextRange,
        });
      }

      return true;
    },
    [
      getSeasonById,
      setDateRangePicTreeNtt,
      setDateRangePicTreeMinMax,
      setSelectedSeasonNtt,
      setSelectedAreaLayerNtt,
      currentDateTime,
      dateRangePicTreeNtt,
      dateRangePicTreeMinMax,
      selectedSeason,
      selectedAreaLayerId,
      seasonOption.value,
      onBudgetSelectionChange,
    ]
  );

  useEffect(() => {
    getSeasonDataNtt();
  }, [getSeasonDataNtt]);

  useEffect(() => {
    if (seasonData.length === 0) {
      return;
    }

    const selectedSeasonId = selectedSeason?.[0]?.seasonId;
    const selected = selectedSeasonId ? getSeasonById(selectedSeasonId) : null;
    const selectedWasAutomatic = selectedSeasonId && automaticDefaultSeasonId === selectedSeasonId;
    if (selected && !selectedWasAutomatic) {
      if (seasonOption.value !== selected.seasonId) {
        setSeasonOption({
          name: selected.seasonName,
          value: selected.seasonId,
        });
      }
      return;
    }

    const nowJst = moment.tz(currentDateTime, "YYYY-MM-DD HH:mm", JST_TIMEZONE);
    const defaultSeason = getDefaultSeason(seasonData, nowJst);
    if (defaultSeason) {
      if (selectedSeasonId === defaultSeason.seasonId) {
        automaticDefaultSeasonId = defaultSeason.seasonId;
        if (seasonOption.value !== defaultSeason.seasonId) {
          setSeasonOption({
            name: defaultSeason.seasonName,
            value: defaultSeason.seasonId,
          });
        }
        return;
      }
      applySeasonSelection(defaultSeason.seasonId, { automatic: true });
    }
  }, [seasonData, selectedSeason, getSeasonById, applySeasonSelection, currentDateTime, seasonOption.value]);

  const handleSelectionChange = (event, value) => {
    const uniqueValue = value.filter((option, index, self) => {
      const isDuplicate =
        self.filter((item) => item.seasonId === option.seasonId).length > 1;
      return !isDuplicate;
    });

    const nowJst = moment.tz(currentDateTime, "YYYY-MM-DD HH:mm", JST_TIMEZONE);
    const validSelections = uniqueValue.filter(
      (option, index, self) =>
        isSeasonStarted(option, nowJst) &&
        self.findIndex((o) => o.seasonId === option.seasonId) === index
    );

    if (validSelections.length > 0) {
      const selectedSeasonsData = seasonData.filter((s) =>
        validSelections.some((item) => item.seasonId === s.seasonId)
      );

      const earliestStart = selectedSeasonsData
        .map((s) => s.seasonStart)
        .reduce((a, b) => (a < b ? a : b));

      const latestEnd = selectedSeasonsData
        .map((s) => s.seasonEnd)
        .reduce((a, b) => (a > b ? a : b));
      const selectedLatestEnd = selectedSeasonsData.some((season) => isRegularSeasonId(season.seasonId))
        ? clampSeasonEndToToday(latestEnd, todayFormatted)
        : moment(getDateOnly(latestEnd)).isAfter(todayFormatted)
          ? todayFormatted
          : getDateOnly(latestEnd);

      automaticDefaultSeasonId = null;

      const nextBounds = [
        getDateOnly(earliestStart),
        getDateOnly(latestEnd),
      ];
      const nextRange = [
        nextBounds[0],
        selectedLatestEnd,
      ];
      if (!sameValues(dateRangePicTreeMinMax || [], nextBounds)) {
        setDateRangePicTreeMinMax(nextBounds);
      }
      if (!sameValues(dateRangePicTreeNtt || [], nextRange)) {
        setDateRangePicTreeNtt(nextRange);
      }

      // ステートを更新
      const selectedIds = (selectedSeason || []).map((selected) => selected.seasonId);
      const nextIds = validSelections.map((selected) => selected.seasonId);
      if (!sameValues(selectedIds, nextIds)) {
        setSelectedSeasonNtt(validSelections);
      }

      if (typeof onBudgetSelectionChange === "function") {
        onBudgetSelectionChange({
          selectedSeason: validSelections,
          dateRange: [getDateOnly(earliestStart), selectedLatestEnd],
        });
      }
    } else {
      automaticDefaultSeasonId = null;
      if ((dateRangePicTreeMinMax || []).length > 0) {
        setDateRangePicTreeMinMax([]);
      }
      if ((dateRangePicTreeNtt || []).length > 0) {
        setDateRangePicTreeNtt([]);
      }

      if (typeof onBudgetSelectionChange === "function") {
        onBudgetSelectionChange({
          selectedSeason: [],
          dateRange: [],
        });
      }
    }
  };

  const handleChange = (event) => {
    applySeasonSelection(event.target.value, { automatic: false });
  };

  const handleLayerChange = (event) => {
    setSelectedAreaLayerNtt(event.target.value);
  };

  return (
    <div className={classes.calendarWrapper}>
      <div className={classes.root}>
        <Grid container spacing={1} direction="row">
          <Grid item spacing={1}>
            シーズン：
          </Grid>
          <Grid item>
            {singlePeriod && (
              <div style={{ width: 300, marginTop: -12 }}>
                <SingleSelect
                  selected={seasonOption}
                  options={seasonData.map((season) => ({
                    name: season.seasonName,
                    value: season.seasonId,
                  }))}
                  handleChange={handleChange}
                />
              </div>
            )}
            {!singlePeriod &&
            <Autocomplete
              multiple
              id="season-autocomplete"
              options={seasonData}
              disableCloseOnSelect
              getOptionLabel={(option) =>
                `${option.seasonName} : ${option.seasonStart} ~ ${option.seasonEnd}`
              }
              renderOption={(option, { selected }) => {
                const isFutureSeason = !isSeasonStarted(
                  option,
                  moment.tz(currentDateTime, "YYYY-MM-DD HH:mm", JST_TIMEZONE)
                );
                const isSelected = selectedSeason.some((selected) => selected.seasonId === option.seasonId);
                return (
                  <div>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={isSelected}
                      disabled={isFutureSeason}
                    />
                    {`${option.seasonName}: `}
                    <span
                      style={{ color: "gray", marginLeft: 16, fontSize: 13 }}
                    >
                      {`${option.seasonStart} ~ ${option.seasonEnd}`}
                    </span>
                  </div>
                );
              }}
              value={selectedSeason || []}
              onChange={handleSelectionChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder=""
                  className={classes.seasonTextField}
                />
              )}
              renderTags={(selected, getTagProps) => {
                const visibleTags = selected.slice(0, 3);
                const extraCount = selected.length - visibleTags.length;
                return [
                  ...visibleTags.map((option, index) => (
                    <Chip
                      key={index}
                      label={option.seasonName}
                      size="small"
                      className={classes.chip}
                    />
                  )),
                  extraCount > 0 && (
                    <Typography
                      key="extra"
                      variant="body2"
                      style={{ marginLeft: 8, color: "gray" }}
                    >
                      +{extraCount}
                    </Typography>
                  ),
                ];
              }}
              disableClearable
              className={classes.autocomplete}
            />
            }
          </Grid>
          {REGULAR_SEASON_IDS.includes(seasonOption.value) &&
            currentPage === REGULAR_BY_AREA_PAGE && (
            <>
            <Grid item spacing={1}>
              階層：
            </Grid>
            <Grid item>
              <div style={{ width: 220, marginTop: -12 }}>
                <SingleSelect
                  selected={{ value: selectedAreaLayerId }}
                  options={REGULAR_LAYER_OPTIONS}
                  handleChange={handleLayerChange}
                />
              </div>
            </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  );
};

PeriodNtt.prototypes = {
  classes: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  periodType: state.pictree.periodType,
  seasonData: state.pictreeNtt.seasonData,
  selectedSeason: state.pictreeNtt.selectedSeason,
  selectedAreaLayerId: state.pictreeNtt.selectedAreaLayerId,
  dateRangePicTreeNtt: state.page.dateRangePicTreeNtt,
  dateRangePicTreeMinMax: state.page.dateRangePicTreeMinMax,
  currentPage: state.page.currentPage,
});

const mapDispatchToProps = (dispatch) => ({
  setDateRangePicTreeNtt: (param) => dispatch(setDateRangePicTreeNtt(param)),
  setDateRangePicTreeMinMax: (param) => dispatch(setDateRangePicTreeMinMax(param)),
  getSeasonDataNtt: () => dispatch(getSeasonDataNtt()),
  setSelectedSeasonNtt: (param) => dispatch(setSelectedSeasonNtt(param)),
  setSelectedAreaLayerNtt: (param) => dispatch(setSelectedAreaLayerNtt(param)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(null, { withTheme: true })(PeriodNtt));
